"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SharedTrackingServiceV2 = void 0;
const utils_1 = require("./utils");
const FEATURE_APP_NAME_UNKNOWN = 'unknown';
class SharedTrackingServiceV2 {
    constructor(datalayer, sanitizeUrl, logger) {
        this.datalayer = datalayer;
        this.sanitizeUrl = sanitizeUrl;
        this.logger = logger;
        this.impressionTrackingMap = new Map();
        this.observer = this.createObserver();
    }
    setFeatureAppName(featureApp) {
        const existingComponent = this.getComponent(featureApp.id);
        if (!existingComponent) {
            this.createComponent(featureApp);
            return;
        }
        existingComponent.componentInfo.componentName = featureApp.name;
    }
    track(featureApp, data) {
        const { event, userUpdate, componentUpdate } = data;
        if (userUpdate) {
            this.updateUser(userUpdate);
        }
        if (componentUpdate) {
            this.updateComponent(featureApp, componentUpdate);
        }
        if (!event.eventInfo || typeof event.eventInfo.eventAction !== 'string') {
            this.logger.warn(`Dropping event with empty eventAction from ${featureApp.name} (id: ${featureApp.id}). This is most likely a bug in the feature app and should be fixed.`, event);
            return;
        }
        if (!this.datalayer.event) {
            this.datalayer.event = [];
        }
        this.datalayer.event.push(this.addEventAttributes(featureApp, event));
    }
    trackInternalNavigation(featureApp, data) {
        const event = (0, utils_1.mergeFirstLevel)(data.event, {
            attributes: {
                targetURL: this.sanitizeUrl(`${window.location.origin}${data.targetHref}`),
            },
        });
        this.track(featureApp, Object.assign(Object.assign({}, data), { event }));
    }
    updateComponent(featureApp, componentUpdate) {
        var _a;
        this.assertComponent(featureApp);
        const componentIndex = this.getComponentIndex(featureApp.id);
        const cleanUpdate = Object.assign(Object.assign({}, componentUpdate), { componentInfo: Object.assign({}, componentUpdate.componentInfo) });
        delete cleanUpdate.componentInfo.componentID;
        delete cleanUpdate.componentInfo.componentName;
        const updatedComponent = (0, utils_1.mergeFirstLevel)(((_a = this.datalayer.component) === null || _a === void 0 ? void 0 : _a[componentIndex]) || {}, cleanUpdate);
        // datalyer.component is defintiely defined after calling `assertComponent`
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.datalayer.component[componentIndex] = updatedComponent;
    }
    updateUser(userUpdate) {
        const updatedUser = (0, utils_1.mergeFirstLevel)(this.datalayer.user || {}, userUpdate);
        this.datalayer.user = updatedUser;
    }
    // eslint-disable-next-line class-methods-use-this
    evaluateLinkType(targetUrl) {
        const internalLinkAction = 'internal_link';
        const externalLinkAction = 'exit_link';
        if (targetUrl.startsWith('/')) {
            return internalLinkAction;
        }
        if (targetUrl.startsWith('http')) {
            if (targetUrl.startsWith(window.location.origin)) {
                return internalLinkAction;
            }
            return externalLinkAction;
        }
        return undefined;
    }
    registerImpressionTracking(featureAppInfo, element, dataCallback) {
        const trackingInfo = {
            featureAppInfo,
            dataCallback,
            fired: false,
            observer: {
                Observer70: { isVisible: false },
                Observer100: { isVisible: false },
            },
        };
        if (!this.impressionTrackingMap.has(element)) {
            this.impressionTrackingMap.set(element, trackingInfo);
            this.observer.observe(element);
        }
        else {
            throw new Error(`The element of the feature app ${featureAppInfo.name} (id: ${featureAppInfo.id}) is already registered: ${element} `);
        }
        return () => {
            this.removeImpressionObservors(element);
            this.impressionTrackingMap.delete(element);
        };
    }
    getDatalayer() {
        return this.datalayer;
    }
    getComponentIndex(featureAppId) {
        var _a, _b;
        return ((_b = (_a = this.datalayer.component) === null || _a === void 0 ? void 0 : _a.findIndex((component) => { var _a; return ((_a = component === null || component === void 0 ? void 0 : component.componentInfo) === null || _a === void 0 ? void 0 : _a.componentID) === featureAppId; })) !== null && _b !== void 0 ? _b : -1);
    }
    getComponent(featureAppId) {
        var _a;
        return (_a = this.datalayer.component) === null || _a === void 0 ? void 0 : _a[this.getComponentIndex(featureAppId)];
    }
    assertComponent(featureApp) {
        if (this.getComponent(featureApp.id)) {
            return;
        }
        throw new Error(`Component for feature app with ID ${featureApp.id} does not exist. Did you forget to set trackingService.featureAppName?`);
    }
    createComponent(featureApp) {
        if (this.getComponent(featureApp.id)) {
            return;
        }
        if (!this.datalayer.component) {
            this.datalayer.component = [];
        }
        this.datalayer.component.push({
            componentInfo: {
                componentID: featureApp.id,
                componentName: featureApp.name || FEATURE_APP_NAME_UNKNOWN,
            },
        });
    }
    removeComponent(featureAppId) {
        var _a;
        const componentIndex = this.getComponentIndex(featureAppId);
        if (componentIndex >= 0) {
            (_a = this.datalayer.component) === null || _a === void 0 ? void 0 : _a.splice(componentIndex, 1);
        }
    }
    addEventAttributes(featureApp, event) {
        this.assertComponent(featureApp);
        const currentURL = this.sanitizeUrl(window.location.href);
        const automaticAttributes = {
            currentURL,
            relatedComponent: {
                featureAppID: featureApp.id,
                featureAppName: featureApp.name || FEATURE_APP_NAME_UNKNOWN,
                componentIndex: this.getComponentIndex(featureApp.id),
            },
        };
        const fullAttributes = (0, utils_1.mergeFirstLevel)(event.attributes, automaticAttributes);
        return Object.assign(Object.assign({}, event), { attributes: fullAttributes });
    }
    createObserver() {
        /* This observer should trigger at any intersection in the upper 70%.
         * With the -30% in the rootMargin we reduce the intersection space to the bottom
         * and only the upper 70% of the space will trigger the observer.
         */
        const observer70 = new IntersectionObserver(this.visibilityCallback.bind(this, 'Observer70'), {
            root: null,
            rootMargin: '0px 0px -30% 0px',
            threshold: 0,
        });
        const observer100 = new IntersectionObserver(this.visibilityCallback.bind(this, 'Observer100'), {
            root: null,
            rootMargin: '0px',
            threshold: 1,
        });
        return {
            observe(node) {
                observer70.observe(node);
                observer100.observe(node);
            },
            unobserve(node) {
                observer70.unobserve(node);
                observer100.unobserve(node);
            },
        };
    }
    visibilityCallback(observerType, entries) {
        const delay = 1000;
        entries.forEach((intersectionEntry) => {
            let isVisible = intersectionEntry.intersectionRatio > 0;
            if (observerType === 'Observer100') {
                isVisible = intersectionEntry.intersectionRatio === 1;
            }
            const element = intersectionEntry.target;
            const elementTrackingInfo = this.impressionTrackingMap.get(element);
            if (!elementTrackingInfo || elementTrackingInfo.fired === true) {
                this.removeImpressionObservors(element);
                return;
            }
            const currentObserverData = elementTrackingInfo.observer[observerType];
            currentObserverData.isVisible = isVisible;
            if (isVisible && !currentObserverData.timeoutId) {
                currentObserverData.timeoutId = setTimeout(this.triggerTracking.bind(this, element, observerType), delay);
            }
            else if (!isVisible && currentObserverData.timeoutId) {
                clearTimeout(currentObserverData.timeoutId);
                currentObserverData.timeoutId = undefined;
            }
        });
    }
    triggerTracking(element, observerType) {
        const trackingInfo = this.impressionTrackingMap.get(element);
        if (trackingInfo &&
            trackingInfo.fired === false &&
            trackingInfo.observer[observerType].isVisible) {
            trackingInfo.fired = true;
            this.removeImpressionObservors(element);
            this.track(trackingInfo.featureAppInfo, trackingInfo.dataCallback());
        }
        else if (!trackingInfo) {
            this.logger.warn(`Could not find tracking info for the element: '${element}'`);
        }
    }
    removeImpressionObservors(element) {
        this.observer.unobserve(element);
        const trackingInfo = this.impressionTrackingMap.get(element);
        if (trackingInfo) {
            Object.values(trackingInfo.observer).forEach((o) => {
                if (o.timeoutId) {
                    clearTimeout(o.timeoutId);
                    // eslint-disable-next-line no-param-reassign
                    o.timeoutId = undefined;
                }
            });
        }
    }
}
exports.SharedTrackingServiceV2 = SharedTrackingServiceV2;
//# sourceMappingURL=shared.js.map